import { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import type { AgentState, TargetKey } from "@/agent";

import { selectAnswersAfter, selectLastAnswerSeq } from "./selectors";

type State = { agent: AgentState };

/**
 * Replies that arrived while the lip was not open. Only the last seen seq is kept, per thread:
 * a thread's history counts as seen when it first loads, and everything is seen while open.
 */
export function useUnseen(key: TargetKey, open: boolean): number {
  const threadId = useSelector((state: State) => state.agent.targets[key]?.threadId ?? null);
  const lastSeq = useSelector((state: State) => selectLastAnswerSeq(state, key));
  const [seen, setSeen] = useState<Record<number, number>>({});

  useEffect(() => {
    if (threadId == null) return;
    setSeen((previous) =>
      (open || !(threadId in previous)) && previous[threadId] !== lastSeq
        ? { ...previous, [threadId]: lastSeq }
        : previous
    );
  }, [threadId, lastSeq, open]);

  const baseline = threadId == null ? undefined : seen[threadId];
  return useSelector((state: State) =>
    baseline === undefined || open ? 0 : selectAnswersAfter(state, key, baseline)
  );
}
